// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Switch } from '@mui/material';
import { Translation } from '@shared/ui-kit';
import { type FC } from 'react';
import { classNames } from '@shared/ui-kit/utils/classnames';

export interface PositionsViewToggleProps {
  readonly viewMode: 'market' | 'product-family';
  readonly onViewModeChange: (viewMode: 'market' | 'product-family') => void;
  readonly dataTestId?: string;
}

export const PositionsViewToggle: FC<PositionsViewToggleProps> = ({
  viewMode,
  onViewModeChange,
  dataTestId = 'positions-view-toggle',
}) => {
  const isMarketActive = viewMode === 'market';
  const isProductFamilyActive = viewMode === 'product-family';

  return (
    <div className="flex items-center gap-2">
      <span
        className={classNames('cursor-pointer text-sm transition-colors', {
          'font-medium text-primary-medium': isMarketActive,
          'text-control-track': !isMarketActive,
        })}
        onClick={() => onViewModeChange('market')}
      >
        <Translation i18nKey="glossary.view-by-market" ns="glossary" />
      </span>
      <Switch
        checked={isProductFamilyActive}
        onChange={event =>
          onViewModeChange(event.target.checked ? 'product-family' : 'market')
        }
        data-testid={dataTestId}
      />
      <span
        className={classNames('cursor-pointer text-sm transition-colors', {
          'font-medium text-primary-medium': isProductFamilyActive,
          'text-control-track': !isProductFamilyActive,
        })}
        onClick={() => onViewModeChange('product-family')}
      >
        <Translation i18nKey="glossary.view-by-product-family" ns="glossary" />
      </span>
    </div>
  );
};
